import React, { useRef, useEffect, useState, useCallback } from "react";

export type DrawingTool =
	| "pen"
	| "circle"
	| "line"
	| "rectangle"
	| "arrow"
	| "eraser";

interface Point {
	x: number;
	y: number;
}

interface Drawing {
	id: string;
	tool: DrawingTool;
	points: Point[];
	color: string;
	lineWidth: number;
	completed: boolean;
}

// Pure geometry helpers — hoisted to module scope so they have stable identity
// and don't pollute hook dependency arrays.
const distanceToLineSegment = (
	point: Point,
	lineStart: Point,
	lineEnd: Point,
): number => {
	const A = point.x - lineStart.x;
	const B = point.y - lineStart.y;
	const C = lineEnd.x - lineStart.x;
	const D = lineEnd.y - lineStart.y;

	const dot = A * C + B * D;
	const lenSq = C * C + D * D;

	if (lenSq === 0) {
		return Math.sqrt(A * A + B * B);
	}

	const param = dot / lenSq;
	let xx, yy;

	if (param < 0) {
		xx = lineStart.x;
		yy = lineStart.y;
	} else if (param > 1) {
		xx = lineEnd.x;
		yy = lineEnd.y;
	} else {
		xx = lineStart.x + param * C;
		yy = lineStart.y + param * D;
	}

	const dx = point.x - xx;
	const dy = point.y - yy;
	return Math.sqrt(dx * dx + dy * dy);
};

const isPointNearRectangle = (
	point: Point,
	rect: { x: number; y: number; width: number; height: number },
	threshold: number,
): boolean => {
	const edges = [
		{
			start: { x: rect.x, y: rect.y },
			end: { x: rect.x + rect.width, y: rect.y },
		}, // top
		{
			start: { x: rect.x + rect.width, y: rect.y },
			end: { x: rect.x + rect.width, y: rect.y + rect.height },
		}, // right
		{
			start: { x: rect.x + rect.width, y: rect.y + rect.height },
			end: { x: rect.x, y: rect.y + rect.height },
		}, // bottom
		{
			start: { x: rect.x, y: rect.y + rect.height },
			end: { x: rect.x, y: rect.y },
		}, // left
	];

	return edges.some(
		(edge) => distanceToLineSegment(point, edge.start, edge.end) <= threshold,
	);
};

interface DrawingOverlayProps {
	isDrawingMode: boolean;
	currentTool: DrawingTool;
	strokeColor: string;
	lineWidth: number;
	onToggleDrawingMode: () => void;
	onToolChange: (tool: DrawingTool) => void;
	onColorChange: (color: string) => void;
	onLineWidthChange: (width: number) => void;
	onClearDrawings: () => void;
}

const DrawingOverlay: React.FC<DrawingOverlayProps> = ({
	isDrawingMode,
	currentTool,
	strokeColor,
	lineWidth,
	onToggleDrawingMode,
	onToolChange,
	onColorChange,
	onLineWidthChange,
	onClearDrawings,
}) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const toolboxRef = useRef<HTMLDivElement>(null);
	const [drawings, setDrawings] = useState<Drawing[]>([]);
	const [isDrawing, setIsDrawing] = useState(false);
	const [currentDrawing, setCurrentDrawing] = useState<Drawing | null>(null);
	const [, setStartPoint] = useState<Point | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
	const [toolboxPosition, setToolboxPosition] = useState<{
		x: number;
		y: number;
	} | null>(null);

	// Colors palette
	const colors = [
		"#ef4444",
		"#f97316",
		"#eab308",
		"#22c55e",
		"#3b82f6",
		"#8b5cf6",
		"#ec4899",
		"#000000",
		"#6b7280",
		"#ffffff",
	];

	const generateId = () => Math.random().toString(36).substr(2, 9);

	// Enhanced hit detection for eraser
	const isPointNearDrawing = useCallback(
		(point: Point, drawing: Drawing): boolean => {
			const threshold = Math.max(drawing.lineWidth + 5, 10); // Minimum 10px hit area

			switch (drawing.tool) {
				case "pen":
					// For pen strokes, check if point is near any line segment
					for (let i = 1; i < drawing.points.length; i++) {
						const dist = distanceToLineSegment(
							point,
							drawing.points[i - 1],
							drawing.points[i],
						);
						if (dist <= threshold) return true;
					}
					return false;

				case "line":
					if (drawing.points.length >= 2) {
						const dist = distanceToLineSegment(
							point,
							drawing.points[0],
							drawing.points[1],
						);
						return dist <= threshold;
					}
					return false;

				case "rectangle":
					if (drawing.points.length >= 2) {
						const [start, end] = drawing.points;
						const rect = {
							x: Math.min(start.x, end.x),
							y: Math.min(start.y, end.y),
							width: Math.abs(end.x - start.x),
							height: Math.abs(end.y - start.y),
						};
						return isPointNearRectangle(point, rect, threshold);
					}
					return false;

				case "circle":
					if (drawing.points.length >= 2) {
						const center = drawing.points[0];
						const edge = drawing.points[1];
						const radius = Math.sqrt(
							Math.pow(edge.x - center.x, 2) + Math.pow(edge.y - center.y, 2),
						);
						const distToCenter = Math.sqrt(
							Math.pow(point.x - center.x, 2) + Math.pow(point.y - center.y, 2),
						);
						return Math.abs(distToCenter - radius) <= threshold;
					}
					return false;

				case "arrow":
					if (drawing.points.length >= 2) {
						// Check main line
						const dist = distanceToLineSegment(
							point,
							drawing.points[0],
							drawing.points[1],
						);
						if (dist <= threshold) return true;

						// Check arrowhead lines
						const [start, end] = drawing.points;
						const angle = Math.atan2(end.y - start.y, end.x - start.x);
						const arrowLength = 15;
						const arrowAngle = Math.PI / 6;

						const arrowPoint1 = {
							x: end.x - arrowLength * Math.cos(angle - arrowAngle),
							y: end.y - arrowLength * Math.sin(angle - arrowAngle),
						};
						const arrowPoint2 = {
							x: end.x - arrowLength * Math.cos(angle + arrowAngle),
							y: end.y - arrowLength * Math.sin(angle + arrowAngle),
						};

						return (
							distanceToLineSegment(point, end, arrowPoint1) <= threshold ||
							distanceToLineSegment(point, end, arrowPoint2) <= threshold
						);
					}
					return false;

				default:
					return false;
			}
		},
		[],
	);

	const getCanvasPosition = useCallback(
		(e: MouseEvent | React.MouseEvent): Point => {
			const canvas = canvasRef.current;
			if (!canvas) return { x: 0, y: 0 };

			const rect = canvas.getBoundingClientRect();
			return {
				x: e.clientX - rect.left,
				y: e.clientY - rect.top,
			};
		},
		[],
	);

	const redrawCanvas = useCallback(() => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext("2d");
		if (!canvas || !ctx) return;

		// Clear canvas
		ctx.clearRect(0, 0, canvas.width, canvas.height);

		// Draw all completed drawings
		drawings.forEach((drawing) => {
			if (drawing.points.length === 0) return;

			ctx.strokeStyle = drawing.color;
			ctx.lineWidth = drawing.lineWidth;
			ctx.lineCap = "round";
			ctx.lineJoin = "round";

			switch (drawing.tool) {
				case "pen":
					if (drawing.points.length > 1) {
						ctx.beginPath();
						ctx.moveTo(drawing.points[0].x, drawing.points[0].y);
						for (let i = 1; i < drawing.points.length; i++) {
							ctx.lineTo(drawing.points[i].x, drawing.points[i].y);
						}
						ctx.stroke();
					}
					break;

				case "line":
					if (drawing.points.length >= 2) {
						ctx.beginPath();
						ctx.moveTo(drawing.points[0].x, drawing.points[0].y);
						ctx.lineTo(drawing.points[1].x, drawing.points[1].y);
						ctx.stroke();
					}
					break;

				case "rectangle":
					if (drawing.points.length >= 2) {
						const start = drawing.points[0];
						const end = drawing.points[1];
						const width = end.x - start.x;
						const height = end.y - start.y;
						ctx.strokeRect(start.x, start.y, width, height);
					}
					break;

				case "circle":
					if (drawing.points.length >= 2) {
						const start = drawing.points[0];
						const end = drawing.points[1];
						const radius = Math.sqrt(
							Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2),
						);
						ctx.beginPath();
						ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI);
						ctx.stroke();
					}
					break;

				case "arrow":
					if (drawing.points.length >= 2) {
						const start = drawing.points[0];
						const end = drawing.points[1];

						// Draw line
						ctx.beginPath();
						ctx.moveTo(start.x, start.y);
						ctx.lineTo(end.x, end.y);
						ctx.stroke();

						// Draw arrowhead
						const angle = Math.atan2(end.y - start.y, end.x - start.x);
						const arrowLength = 15;
						const arrowAngle = Math.PI / 6;

						ctx.beginPath();
						ctx.moveTo(end.x, end.y);
						ctx.lineTo(
							end.x - arrowLength * Math.cos(angle - arrowAngle),
							end.y - arrowLength * Math.sin(angle - arrowAngle),
						);
						ctx.moveTo(end.x, end.y);
						ctx.lineTo(
							end.x - arrowLength * Math.cos(angle + arrowAngle),
							end.y - arrowLength * Math.sin(angle + arrowAngle),
						);
						ctx.stroke();
					}
					break;
			}
		});

		// Draw current drawing in progress
		if (currentDrawing && currentDrawing.points.length > 0) {
			ctx.strokeStyle = currentDrawing.color;
			ctx.lineWidth = currentDrawing.lineWidth;
			ctx.lineCap = "round";
			ctx.lineJoin = "round";

			switch (currentDrawing.tool) {
				case "pen":
					if (currentDrawing.points.length > 1) {
						ctx.beginPath();
						ctx.moveTo(currentDrawing.points[0].x, currentDrawing.points[0].y);
						for (let i = 1; i < currentDrawing.points.length; i++) {
							ctx.lineTo(
								currentDrawing.points[i].x,
								currentDrawing.points[i].y,
							);
						}
						ctx.stroke();
					}
					break;

				case "line":
					if (currentDrawing.points.length >= 2) {
						ctx.beginPath();
						ctx.moveTo(currentDrawing.points[0].x, currentDrawing.points[0].y);
						ctx.lineTo(currentDrawing.points[1].x, currentDrawing.points[1].y);
						ctx.stroke();
					}
					break;

				case "rectangle":
					if (currentDrawing.points.length >= 2) {
						const start = currentDrawing.points[0];
						const end = currentDrawing.points[1];
						const width = end.x - start.x;
						const height = end.y - start.y;
						ctx.strokeRect(start.x, start.y, width, height);
					}
					break;

				case "circle":
					if (currentDrawing.points.length >= 2) {
						const start = currentDrawing.points[0];
						const end = currentDrawing.points[1];
						const radius = Math.sqrt(
							Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2),
						);
						ctx.beginPath();
						ctx.arc(start.x, start.y, radius, 0, 2 * Math.PI);
						ctx.stroke();
					}
					break;

				case "arrow":
					if (currentDrawing.points.length >= 2) {
						const start = currentDrawing.points[0];
						const end = currentDrawing.points[1];

						// Draw line
						ctx.beginPath();
						ctx.moveTo(start.x, start.y);
						ctx.lineTo(end.x, end.y);
						ctx.stroke();

						// Draw arrowhead
						const angle = Math.atan2(end.y - start.y, end.x - start.x);
						const arrowLength = 15;
						const arrowAngle = Math.PI / 6;

						ctx.beginPath();
						ctx.moveTo(end.x, end.y);
						ctx.lineTo(
							end.x - arrowLength * Math.cos(angle - arrowAngle),
							end.y - arrowLength * Math.sin(angle - arrowAngle),
						);
						ctx.moveTo(end.x, end.y);
						ctx.lineTo(
							end.x - arrowLength * Math.cos(angle + arrowAngle),
							end.y - arrowLength * Math.sin(angle + arrowAngle),
						);
						ctx.stroke();
					}
					break;
			}
		}
	}, [drawings, currentDrawing]);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			if (!isDrawingMode || isDragging || !toolboxPosition) return;

			const point = getCanvasPosition(e);
			setIsDrawing(true);
			setStartPoint(point);

			if (currentTool === "eraser") {
				// Find and remove drawing at this point with better hit detection
				setDrawings((prev) =>
					prev.filter((drawing) => {
						return !isPointNearDrawing(point, drawing);
					}),
				);
				return;
			}

			const newDrawing: Drawing = {
				id: generateId(),
				tool: currentTool,
				points: [point],
				color: strokeColor,
				lineWidth: lineWidth,
				completed: false,
			};

			setCurrentDrawing(newDrawing);
		},
		[
			isDrawingMode,
			currentTool,
			strokeColor,
			lineWidth,
			getCanvasPosition,
			isDragging,
			toolboxPosition,
			isPointNearDrawing,
		],
	);

	const handleMouseMove = useCallback(
		(e: React.MouseEvent) => {
			if (
				!isDrawing ||
				!currentDrawing ||
				!isDrawingMode ||
				currentTool === "eraser" ||
				!toolboxPosition
			)
				return;

			const point = getCanvasPosition(e);

			if (currentTool === "pen") {
				// For pen, add points continuously
				setCurrentDrawing((prev) =>
					prev
						? {
								...prev,
								points: [...prev.points, point],
							}
						: null,
				);
			} else {
				// For shapes, update the end point
				setCurrentDrawing((prev) =>
					prev
						? {
								...prev,
								points: [prev.points[0], point],
							}
						: null,
				);
			}
		},
		[
			isDrawing,
			currentDrawing,
			isDrawingMode,
			currentTool,
			getCanvasPosition,
			toolboxPosition,
		],
	);

	const handleMouseUp = useCallback(() => {
		if (!isDrawing || !currentDrawing || !isDrawingMode) return;

		setIsDrawing(false);

		// Complete the drawing and add it to the list
		const completedDrawing = { ...currentDrawing, completed: true };
		setDrawings((prev) => [...prev, completedDrawing]);
		setCurrentDrawing(null);
		setStartPoint(null);
	}, [isDrawing, currentDrawing, isDrawingMode]);

	// Handle canvas resize
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const resizeCanvas = () => {
			const container = canvas.parentElement;
			if (container) {
				canvas.width = container.offsetWidth;
				canvas.height = container.offsetHeight;
				redrawCanvas();
			}
		};

		resizeCanvas();
		window.addEventListener("resize", resizeCanvas);

		return () => window.removeEventListener("resize", resizeCanvas);
	}, [redrawCanvas]);

	// Redraw when drawings change
	useEffect(() => {
		redrawCanvas();
	}, [drawings, currentDrawing, redrawCanvas]);

	const clearDrawings = () => {
		setDrawings([]);
		setCurrentDrawing(null);
		onClearDrawings();
	};

	// Toolbox drag handlers
	const handleDragHandleMouseDown = useCallback((e: React.MouseEvent) => {
		// Only start dragging if clicking on the drag handle itself
		const target = e.target as HTMLElement;
		if (target.closest(".drag-handle")) {
			setIsDragging(true);
			const rect = toolboxRef.current?.getBoundingClientRect();
			if (rect) {
				setDragOffset({
					x: e.clientX - rect.left,
					y: e.clientY - rect.top,
				});
			}
			e.preventDefault();
			e.stopPropagation();
		}
	}, []);

	const handleToolboxMouseMove = useCallback(
		(e: MouseEvent) => {
			if (isDragging && toolboxRef.current && canvasRef.current) {
				const containerRect = canvasRef.current.getBoundingClientRect();
				const newX = e.clientX - containerRect.left - dragOffset.x;
				const newY = e.clientY - containerRect.top - dragOffset.y;

				// Keep toolbox within bounds
				const toolboxRect = toolboxRef.current.getBoundingClientRect();
				const maxX = containerRect.width - toolboxRect.width;
				const maxY = containerRect.height - toolboxRect.height;

				setToolboxPosition({
					x: Math.max(0, Math.min(newX, maxX)),
					y: Math.max(0, Math.min(newY, maxY)),
				});
			}
		},
		[isDragging, dragOffset],
	);

	const handleToolboxMouseUp = useCallback(() => {
		setIsDragging(false);
	}, []);

	// Add global mouse event listeners for dragging
	useEffect(() => {
		if (isDragging) {
			document.addEventListener("mousemove", handleToolboxMouseMove);
			document.addEventListener("mouseup", handleToolboxMouseUp);
			return () => {
				document.removeEventListener("mousemove", handleToolboxMouseMove);
				document.removeEventListener("mouseup", handleToolboxMouseUp);
			};
		}
	}, [isDragging, handleToolboxMouseMove, handleToolboxMouseUp]);

	// Set initial position to top right corner
	useEffect(() => {
		if (
			isDrawingMode &&
			!toolboxPosition &&
			toolboxRef.current &&
			canvasRef.current
		) {
			const timeoutId = setTimeout(() => {
				const toolbox = toolboxRef.current;
				const canvas = canvasRef.current;
				if (toolbox && canvas) {
					const canvasRect = canvas.getBoundingClientRect();
					const toolboxRect = toolbox.getBoundingClientRect();
					setToolboxPosition({
						x: canvasRect.width - toolboxRect.width - 8,
						y: 8,
					});
				}
			}, 100); // Small delay to ensure DOM is ready
			return () => clearTimeout(timeoutId);
		}
	}, [isDrawingMode, toolboxPosition]);

	if (!isDrawingMode) return null;

	return (
		<div className="absolute inset-0 z-10 pointer-events-none">
			{/* Drawing Toolbar */}
			<div
				ref={toolboxRef}
				className={`absolute bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-3 pointer-events-auto transition-all duration-200 ${
					isDragging
						? "cursor-move select-none scale-105 shadow-3xl"
						: "hover:shadow-3xl"
				}`}
				style={{
					left: `${toolboxPosition?.x || 8}px`,
					top: `${toolboxPosition?.y || 8}px`,
					zIndex: 50,
				}}
			>
				{/* Header with Exit and Drag */}
				<div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200/50 dark:border-gray-700/50">
					<div className="flex items-center gap-2">
						<div className="w-2 h-2 rounded-full bg-red-500"></div>
						<span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
							Drawing Tools
						</span>
					</div>

					<div className="flex items-center gap-1">
						<div
							className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 cursor-move drag-handle transition-all duration-200 group"
							onMouseDown={handleDragHandleMouseDown}
							title="Drag to move toolbox"
						>
							<svg
								className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
								/>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M6 12h.01M13 12h.01M20 12h.01M6 13a1 1 0 110-2 1 1 0 010 2zm7 0a1 1 0 110-2 1 1 0 010 2zm7 0a1 1 0 110-2 1 1 0 010 2z"
								/>
							</svg>
						</div>

						<button
							onClick={onToggleDrawingMode}
							className="p-1.5 rounded-lg text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 group"
							title="Exit drawing mode"
						>
							<svg
								className="w-4 h-4 group-hover:scale-110 transition-transform"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>
				</div>

				{/* Main Tools Grid */}
				<div className="space-y-3">
					{/* Drawing Tools */}
					<div className="space-y-2">
						<label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
							Tools
						</label>
						<div className="grid grid-cols-6 gap-1.5">
							<button
								onClick={() => onToolChange("pen")}
								className={`p-2.5 rounded-xl transition-all duration-200 group ${
									currentTool === "pen"
										? "bg-blue-500 text-white shadow-lg shadow-blue-500/25 scale-105"
										: "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-105"
								}`}
								title="Free pen"
							>
								<svg
									className="w-4 h-4 group-hover:rotate-12 transition-transform"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
									/>
								</svg>
							</button>

							<button
								onClick={() => onToolChange("line")}
								className={`p-2.5 rounded-xl transition-all duration-200 group ${
									currentTool === "line"
										? "bg-blue-500 text-white shadow-lg shadow-blue-500/25 scale-105"
										: "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-105"
								}`}
								title="Line"
							>
								<svg
									className="w-4 h-4 group-hover:rotate-45 transition-transform"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M4 12h16"
									/>
								</svg>
							</button>

							<button
								onClick={() => onToolChange("rectangle")}
								className={`p-2.5 rounded-xl transition-all duration-200 group ${
									currentTool === "rectangle"
										? "bg-blue-500 text-white shadow-lg shadow-blue-500/25 scale-105"
										: "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-105"
								}`}
								title="Rectangle"
							>
								<svg
									className="w-4 h-4 group-hover:rotate-12 transition-transform"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<rect
										x="3"
										y="3"
										width="18"
										height="18"
										rx="2"
										ry="2"
										strokeWidth="2"
									/>
								</svg>
							</button>

							<button
								onClick={() => onToolChange("circle")}
								className={`p-2.5 rounded-xl transition-all duration-200 group ${
									currentTool === "circle"
										? "bg-blue-500 text-white shadow-lg shadow-blue-500/25 scale-105"
										: "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-105"
								}`}
								title="Circle"
							>
								<svg
									className="w-4 h-4 group-hover:scale-110 transition-transform"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<circle cx="12" cy="12" r="10" strokeWidth="2" />
								</svg>
							</button>

							<button
								onClick={() => onToolChange("arrow")}
								className={`p-2.5 rounded-xl transition-all duration-200 group ${
									currentTool === "arrow"
										? "bg-blue-500 text-white shadow-lg shadow-blue-500/25 scale-105"
										: "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-105"
								}`}
								title="Arrow"
							>
								<svg
									className="w-4 h-4 group-hover:translate-x-1 transition-transform"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M17 8l4 4m0 0l-4 4m4-4H3"
									/>
								</svg>
							</button>

							<button
								onClick={() => onToolChange("eraser")}
								className={`p-2.5 rounded-xl transition-all duration-200 group ${
									currentTool === "eraser"
										? "bg-red-500 text-white shadow-lg shadow-red-500/25 scale-105"
										: "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-105"
								}`}
								title="Eraser"
							>
								<svg
									className="w-4 h-4 group-hover:rotate-12 transition-transform"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
									/>
								</svg>
							</button>
						</div>
					</div>

					{/* Color Palette */}
					<div className="space-y-2">
						<label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
							Colors
						</label>
						<div className="grid grid-cols-5 gap-1.5">
							{colors.map((color) => (
								<button
									key={color}
									onClick={() => onColorChange(color)}
									className={`w-8 h-8 rounded-lg border-2 transition-all duration-200 hover:scale-110 ${
										strokeColor === color
											? "border-gray-800 dark:border-white shadow-lg scale-110 ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-300"
											: "border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400"
									}`}
									style={{ backgroundColor: color }}
									title={`Color: ${color}`}
								/>
							))}
						</div>
					</div>

					{/* Line Width */}
					<div className="space-y-2">
						<label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
							Brush Size: {lineWidth}px
						</label>
						<div className="relative">
							<input
								type="range"
								min="1"
								max="10"
								value={lineWidth}
								onChange={(e) => onLineWidthChange(parseInt(e.target.value))}
								className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full appearance-none cursor-pointer slider-thumb"
							/>
							<div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mt-1">
								<span>1px</span>
								<span>10px</span>
							</div>
						</div>
					</div>

					{/* Actions */}
					<div className="pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
						<button
							onClick={clearDrawings}
							className="w-full px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm font-medium rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-all duration-200 flex items-center justify-center gap-2 group"
							title="Clear all drawings"
						>
							<svg
								className="w-4 h-4 group-hover:rotate-12 transition-transform"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
								/>
							</svg>
							Clear All
						</button>
					</div>
				</div>
			</div>

			{/* Drawing Canvas */}
			<canvas
				ref={canvasRef}
				className={`absolute inset-0 w-full h-full pointer-events-auto ${
					!toolboxPosition
						? "cursor-wait"
						: currentTool === "eraser"
							? "cursor-pointer"
							: "cursor-crosshair"
				}`}
				onMouseDown={handleMouseDown}
				onMouseMove={handleMouseMove}
				onMouseUp={handleMouseUp}
				onMouseLeave={handleMouseUp}
			/>
		</div>
	);
};

export default DrawingOverlay;
